import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { venuesAPI } from '../services/api';
import { useForm } from 'react-hook-form';

const PITCH_TYPES = ['batting', 'bowling', 'balanced', 'spinning', 'seaming'];

function VenueForm({ venue, onClose }) {
  const queryClient = useQueryClient();
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: venue || {
      name: '',
      city: '',
      country: '',
      capacity: 0,
      pitch_type: 'balanced',
      floodlights: false,
      practice_nets: false,
      dressing_rooms: 2,
    }
  });

  const createMutation = useMutation({
    mutationFn: venuesAPI.createVenue,
    onSuccess: () => {
      queryClient.invalidateQueries(['venues']);
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => venuesAPI.updateVenue(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['venues']);
      onClose();
    },
  });

  const onSubmit = (data) => {
    if (venue?.id) {
      updateMutation.mutate({ id: venue.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">{venue ? 'Edit Venue' : 'Add Venue'}</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">City *</label>
              <input
                {...register('city', { required: 'City is required' })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.city && <p className="text-red-500 text-sm">{errors.city.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Country *</label>
              <input
                {...register('country', { required: 'Country is required' })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.country && <p className="text-red-500 text-sm">{errors.country.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Capacity</label>
              <input
                type="number"
                {...register('capacity', { valueAsNumber: true })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Pitch Type</label>
              <select
                {...register('pitch_type')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                {PITCH_TYPES.map(type => (
                  <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Dressing Rooms</label>
              <input
                type="number"
                {...register('dressing_rooms', { valueAsNumber: true })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Ground Dimensions</label>
              <input
                {...register('ground_dimensions')}
                placeholder="e.g., 150m x 140m"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Address</label>
              <input
                {...register('address')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <label className="flex items-center">
              <input type="checkbox" {...register('floodlights')} className="rounded border-gray-300" />
              <span className="ml-2 text-sm text-gray-700">Floodlights</span>
            </label>
            <label className="flex items-center">
              <input type="checkbox" {...register('practice_nets')} className="rounded border-gray-300" />
              <span className="ml-2 text-sm text-gray-700">Practice Nets</span>
            </label>
            <label className="flex items-center">
              <input type="checkbox" {...register('indoor_facility')} className="rounded border-gray-300" />
              <span className="ml-2 text-sm text-gray-700">Indoor Facility</span>
            </label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Venues() {
  const [showForm, setShowForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [filters, setFilters] = useState({ country: '', has_floodlights: '' });
  const queryClient = useQueryClient();

  const { data: venues = [], isLoading } = useQuery({
    queryKey: ['venues', filters],
    queryFn: () => venuesAPI.getVenues(filters),
  });

  const deleteMutation = useMutation({
    mutationFn: venuesAPI.deleteVenue,
    onSuccess: () => queryClient.invalidateQueries(['venues']),
  });

  const handleEdit = (venue) => {
    setEditingVenue(venue);
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingVenue(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold">Venues</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 w-full sm:w-auto text-center"
        >
          Add Venue
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
        <input
          type="text"
          placeholder="Filter by country..."
          value={filters.country}
          onChange={(e) => setFilters({ ...filters, country: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-md w-full sm:w-auto"
        />
        <select
          value={filters.has_floodlights}
          onChange={(e) => setFilters({ ...filters, has_floodlights: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-md w-full sm:w-auto"
        >
          <option value="">All Venues</option>
          <option value="true">With Floodlights</option>
          <option value="false">Without Floodlights</option>
        </select>
      </div>

      {isLoading ? (
        <p>Loading venues...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {venues.map((venue) => (
            <div key={venue.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{venue.name}</h3>
                  <p className="text-gray-600">{venue.city}, {venue.country}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded ${venue.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {venue.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>Capacity:</strong> {venue.capacity?.toLocaleString() || 'N/A'}</p>
                <p><strong>Pitch:</strong> {venue.pitch_type}</p>
                <p><strong>Matches Played:</strong> {venue.matches_played}</p>
                <div className="flex space-x-2 mt-2">
                  {venue.floodlights && <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">Floodlights</span>}
                  {venue.practice_nets && <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Nets</span>}
                </div>
              </div>

              {venue.avg_first_innings_score && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-600">
                    <strong>Avg 1st Innings:</strong> {Math.round(venue.avg_first_innings_score)}
                  </p>
                  {venue.avg_second_innings_score && (
                    <p className="text-sm text-gray-600">
                      <strong>Avg 2nd Innings:</strong> {Math.round(venue.avg_second_innings_score)}
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-2 mt-4 pt-4 border-t">
                <button
                  onClick={() => handleEdit(venue)}
                  className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Delete this venue?')) {
                      deleteMutation.mutate(venue.id);
                    }
                  }}
                  className="px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <VenueForm venue={editingVenue} onClose={handleClose} />}
    </div>
  );
}
